<script lang="ts">
  import type { Companion } from "../../lib/types";
  import { tripsApi, usersApi, type UserSummary } from "../../lib/api";
  import { addNotification } from "../../lib/stores";
  import Modal from "../overlay/Modal.svelte";

  let {
    tripId,
    companions = $bindable([]),
    isOwner = false,
    onChange,
  }: {
    tripId: string;
    companions?: Companion[];
    isOwner?: boolean;
    onChange?: (companions: Companion[]) => void;
  } = $props();

  let showAddModal = $state(false);
  let searchQuery = $state("");
  let searchResults: UserSummary[] = $state([]);
  let searching = $state(false);
  let adding = $state(false);
  let removing: string | null = $state(null);

  let searchTimeout: ReturnType<typeof setTimeout>;
  
  async function handleSearch() {
    if (searchQuery.length < 2) {
      searchResults = [];
      return;
    }

    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(async () => {
      searching = true;
      try {
        const response = await usersApi.search(searchQuery);
        const companionUserIds = companions.map(c => c.user.id);
        searchResults = response.data.filter(u => !companionUserIds.includes(u.id));
      } catch (error) {
        console.error("Search failed:", error);
        searchResults = [];
      } finally {
        searching = false;
      }
    }, 300);
  }

  async function addCompanion(user: UserSummary) {
    adding = true;
    try {
      const response = await tripsApi.addCompanion(tripId, user.id);
      companions = [...companions, response.data];
      addNotification("success", `${user.name}님이 동행자로 추가되었습니다.`);
      showAddModal = false;
      searchQuery = "";
      searchResults = [];
      onChange?.(companions);
    } catch (error: any) {
      const message = error.response?.data?.errors?.[0]?.message || "동행자 추가에 실패했습니다.";
      addNotification("error", message);
    } finally {
      adding = false;
    }
  }

  async function removeCompanion(companion: Companion) {
    if (companion.role === "owner") {
      addNotification("error", "여행 소유자는 제거할 수 없습니다.");
      return;
    }

    removing = companion.id;
    try {
      await tripsApi.removeCompanion(tripId, companion.id);
      companions = companions.filter(c => c.id !== companion.id);
      addNotification("success", `${companion.user.name}님이 동행자에서 제거되었습니다.`);
      onChange?.(companions);
    } catch (error: any) {
      const message = error.response?.data?.errors?.[0]?.message || "동행자 제거에 실패했습니다.";
      addNotification("error", message);
    } finally {
      removing = null;
    }
  }

  function getRoleLabel(role: string): string {
    switch (role) {
      case "owner": return "소유자";
      case "member": return "멤버";
      case "viewer": return "뷰어";
      default: return role;
    }
  }

  function getRoleColor(role: string): string {
    switch (role) {
      case "owner": return "companion-manager__badge companion-manager__badge--owner";
      case "member": return "companion-manager__badge companion-manager__badge--member";
      case "viewer": return "companion-manager__badge companion-manager__badge--viewer";
      default: return "companion-manager__badge companion-manager__badge--viewer";
    }
  }
</script>

<div class="companion-manager rounded-xl p-4">
  <div class="flex items-center justify-between mb-4">
    <h3 class="text-lg font-semibold companion-manager__title flex items-center gap-2">
      <svg class="w-5 h-5 companion-manager__accent" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
      여행 멤버
      <span class="text-sm font-normal companion-manager__meta">({companions.length}명)</span>
    </h3>
    {#if isOwner}
      <button
        onclick={() => showAddModal = true}
        class="flex items-center gap-1 px-3 py-1.5 companion-manager__primary-button text-sm rounded-lg transition-colors"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        추가
      </button>
    {/if}
  </div>

  <div class="space-y-2">
    {#each companions as companion (companion.id)}
      <div class="flex items-center justify-between p-3 rounded-lg companion-manager__row transition-colors">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-medium">
            {#if companion.user.avatarUrl}
              <img src={companion.user.avatarUrl} alt={companion.user.name} class="w-full h-full rounded-full object-cover" />
            {:else}
              {companion.user.name.charAt(0)}
            {/if}
          </div>
          <div>
            <p class="font-medium companion-manager__title">{companion.user.name}</p>
            {#if companion.user.nickname}
              <p class="text-xs companion-manager__meta">@{companion.user.nickname}</p>
            {/if}
          </div>
        </div>
        <div class="flex items-center gap-2">
          <span class="px-2 py-1 text-xs font-medium rounded-full {getRoleColor(companion.role)}">
            {getRoleLabel(companion.role)}
          </span>
          {#if isOwner && companion.role !== "owner"}
            <button
              onclick={() => removeCompanion(companion)}
              disabled={removing === companion.id}
              class="p-1.5 rounded-lg transition-colors disabled:opacity-50 companion-manager__icon-button"
              title="제거"
              aria-label="동행자 제거"
            >
              {#if removing === companion.id}
                <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              {:else}
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              {/if}
            </button>
          {/if}
        </div>
      </div>
    {/each}
  </div>

  {#if companions.length === 0}
    <div class="text-center py-8 companion-manager__meta">
      <p>아직 동행자가 없습니다.</p>
      {#if isOwner}
        <button
          onclick={() => showAddModal = true}
          class="mt-2 companion-manager__link"
        >
          동행자 추가하기
        </button>
      {/if}
    </div>
  {/if}
</div>

<!-- Add Companion Modal -->
<Modal
  show={showAddModal}
  title="동행자 추가"
  onClose={() => { showAddModal = false; searchQuery = ""; searchResults = []; }}
>
  <div class="space-y-4">
    <div class="relative">
      <input
        type="text"
        bind:value={searchQuery}
        oninput={handleSearch}
        placeholder="이름 또는 닉네임으로 검색..."
        class="w-full pl-10 pr-4 py-3 rounded-xl companion-manager__input"
      />
      <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 companion-manager__meta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      {#if searching}
        <svg class="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 companion-manager__meta animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      {/if}
    </div>

    {#if searchQuery.length > 0 && searchQuery.length < 2}
      <p class="text-sm companion-manager__meta text-center">2글자 이상 입력해주세요</p>
    {/if}

    {#if searchResults.length > 0}
      <div class="max-h-64 overflow-y-auto space-y-2">
        {#each searchResults as user (user.id)}
          <button
            onclick={() => addCompanion(user)}
            disabled={adding}
            class="w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left disabled:opacity-50 companion-manager__search-result"
          >
            <div class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-medium">
              {#if user.avatar_url}
                <img src={user.avatar_url} alt={user.name} class="w-full h-full rounded-full object-cover" />
              {:else}
                {user.name.charAt(0)}
              {/if}
            </div>
            <div class="flex-1">
              <p class="font-medium companion-manager__title">{user.name}</p>
              {#if user.nickname}
                <p class="text-xs companion-manager__meta">@{user.nickname}</p>
              {/if}
            </div>
            <svg class="w-5 h-5 companion-manager__accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        {/each}
      </div>
    {:else if searchQuery.length >= 2 && !searching}
      <p class="text-sm companion-manager__meta text-center py-4">검색 결과가 없습니다</p>
    {/if}
  </div>
</Modal>

<style>
  .companion-manager {
    background: var(--surface);
    color: var(--text-primary);
    border: 1px solid var(--border);
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
  }

  .companion-manager__title {
    color: var(--text-primary);
  }

  .companion-manager__meta {
    color: var(--text-secondary);
  }

  .companion-manager__accent {
    color: var(--accent);
  }

  .companion-manager__primary-button {
    background: var(--accent);
    color: #fff;
  }

  .companion-manager__primary-button:hover {
    background: var(--accent-hover);
  }

  .companion-manager__row,
  .companion-manager__search-result {
    background: var(--surface-hover);
  }

  .companion-manager__row:hover,
  .companion-manager__search-result:hover {
    background: color-mix(in srgb, var(--surface-hover) 70%, var(--accent-dim) 30%);
  }

  .companion-manager__icon-button {
    color: var(--text-muted);
  }

  .companion-manager__icon-button:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.08);
  }

  .companion-manager__link {
    color: var(--accent);
  }

  .companion-manager__link:hover {
    text-decoration: underline;
  }

  .companion-manager__badge {
    border: 1px solid transparent;
  }

  .companion-manager__badge--owner {
    background: #fff4cc;
    color: #9a6700;
    border-color: #f3d98b;
  }

  .companion-manager__badge--member {
    background: #e8f1ff;
    color: #1d4ed8;
    border-color: #bfd5ff;
  }

  .companion-manager__badge--viewer {
    background: #eef2f7;
    color: #475569;
    border-color: #d7dee8;
  }

  .companion-manager__input {
    background: var(--surface);
    color: var(--text-primary);
    border: 1px solid var(--border-bright);
  }

  .companion-manager__input::placeholder {
    color: var(--text-muted);
  }

  .companion-manager__input:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 18%, transparent);
  }

  @media (prefers-color-scheme: dark) {
    .companion-manager {
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.32);
    }

    .companion-manager__row:hover,
    .companion-manager__search-result:hover {
      background: color-mix(in srgb, var(--surface-hover) 82%, white 18%);
    }

    .companion-manager__icon-button:hover {
      background: rgba(239, 68, 68, 0.14);
    }

    .companion-manager__badge--owner {
      background: rgba(245, 158, 11, 0.16);
      color: #fcd34d;
      border-color: rgba(245, 158, 11, 0.28);
    }

    .companion-manager__badge--member {
      background: rgba(59, 130, 246, 0.18);
      color: #93c5fd;
      border-color: rgba(59, 130, 246, 0.3);
    }

    .companion-manager__badge--viewer {
      background: rgba(148, 163, 184, 0.16);
      color: #cbd5e1;
      border-color: rgba(148, 163, 184, 0.24);
    }
  }
</style>
